'use client';

import { FC } from 'react';
import { shallow } from 'zustand/shallow';

import { Button } from 'src/general/components/Button/Button';
import { HighlightedFeatureCarousel } from 'src/pdp/components/HighlightedFeatureCarousel/HighlightedFeatureCarousel';
import { formatFeatures } from 'src/pdp/sections/Pdp/pdpHelpers';
import { pdpStore } from 'src/stores/pdpStore';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

const MAX_FEATURES_ITEM = 6;

type Props = {
  productDetails: ProductDetailsPayload;
};

export const Features: FC<Props> = ({ productDetails }) => {
  const { setCurrentModal } = pdpStore(
    ({ setCurrentModal }) => ({
      setCurrentModal,
    }),
    shallow,
  );

  const { detailedHighlightedFeatures, carFeatures } = formatFeatures(productDetails);

  const popularFeaturesCount = detailedHighlightedFeatures.length;
  const optionalFeaturesCount = carFeatures.optionalFeatures.length;
  const aftermarketFeaturesCount = carFeatures.aftermarketFeatures.length;
  const standardFeaturesCount = carFeatures.standardFeatures.length;
  const hasAdditionalFeatures = !!(optionalFeaturesCount || aftermarketFeaturesCount || standardFeaturesCount);

  const numOfFeatures = aftermarketFeaturesCount + optionalFeaturesCount + standardFeaturesCount;

  const handleOpenModal = () => {
    setCurrentModal({ name: 'features', initialTab: 'features' });
    pushToDataLayer({
      event: 'pdp_interaction',
      type: 'modal',
      value: 'view_all_features',
    });
  };

  if (numOfFeatures === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-8 pb-14">
      <header>
        <h3 className="c-fs-h5 font-extrabold">Features we think you’ll love</h3>
      </header>
      {!!popularFeaturesCount && (
        <div data-testid={`feature_popular_features`}>
          <div className="mb-2">
            <h4 className="c-fs-h6 font-extrabold">Popular features</h4>
          </div>
          <HighlightedFeatureCarousel features={detailedHighlightedFeatures} />
        </div>
      )}
      {hasAdditionalFeatures && (
        <dl className="flex flex-col gap-8">
          <FeatureCategory
            title="Installed options"
            count={optionalFeaturesCount}
            items={carFeatures.optionalFeatures.slice(0, MAX_FEATURES_ITEM)}
          />
          <FeatureCategory
            title="Aftermarket accessories"
            count={aftermarketFeaturesCount}
            items={carFeatures.aftermarketFeatures.slice(0, MAX_FEATURES_ITEM)}
          />
          <FeatureCategory
            title="Standard features"
            count={standardFeaturesCount}
            items={carFeatures.standardFeatures.slice(0, MAX_FEATURES_ITEM)}
          />
        </dl>
      )}
      <footer>
        <Button variant="secondary" size="small" onClick={handleOpenModal} data-testid="Features-viewAll">
          View all {numOfFeatures} features
        </Button>
      </footer>
    </section>
  );
};

interface FeatureCategoryProps {
  title: string;
  count: number;
  items: string[];
}

const FeatureCategory: FC<FeatureCategoryProps> = ({ title, count, items }) =>
  count ? (
    <div className="flex flex-col gap-4">
      <dt className="c-fs-h6 font-extrabold" data-testid={`feature_${title.replace(' ', '_')}`}>
        {title} <span data-testid={`feature_${title.replace(' ', '_')}_count`}>({count})</span>
      </dt>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 smp:grid-cols-3">
        {items.map((item) => (
          <dd key={item} className="c-fs-body2" data-testid={`feature_${title.replace(' ', '_')}_items`}>
            {item}
          </dd>
        ))}
      </div>
    </div>
  ) : null;
